import React, { useEffect, useRef, useState } from 'react';
import IIndexBase from '../models/Indexes/iIndexBase';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { GridApiCommunity } from '@mui/x-data-grid/models/api/gridApiCommunity';

interface SyncedScrollBarProps {
  //scrollbar to sync to
  apiRef: React.MutableRefObject<GridApiCommunity>;

  //columns and product group of a datagrid, for just for rerendering
  columns: GridColDef[];
  products: IIndexBase[];
}

//Component to duplicate a MUI DataGrid horizontal scrollbar
const SyncedScrollBar: React.FC<SyncedScrollBarProps> = ({ apiRef, columns, products }) => {
  const topScrollRef = useRef<HTMLDivElement>(null);
  const [dataGridContentWidth, setDataGridContentWidth] = useState(0);
  const [showTopScrollbar, setShowTopScrollbar] = useState(false);
  const [dataGridScroller, setDataGridScroller] = useState<HTMLDivElement | null>(null);

  // Checks if the scroller is present and updates the width whenever it changes
  // In charge of visibility and width of the top scrollbar (resize observer)
  useEffect(() => {
    const rootElement = apiRef.current.rootElementRef?.current;
    if (!rootElement) return;

    const updateScroller = () => {
      const rootElement = apiRef.current.rootElementRef?.current;
      if (!rootElement) {
        setDataGridScroller(null);
        setShowTopScrollbar(false);
        return;
      }

      const scroller = rootElement.querySelector('.MuiDataGrid-scrollbar--horizontal') as HTMLDivElement | null;
      setDataGridScroller(scroller);
      
      const isScrollbarVisible = scroller && scroller.scrollWidth > scroller.clientWidth;
      setShowTopScrollbar(isScrollbarVisible ?? false);

      if (scroller) {
        setDataGridContentWidth(scroller.scrollWidth);
        if (topScrollRef.current && topScrollRef.current.scrollLeft !== scroller.scrollLeft) {
          topScrollRef.current.scrollLeft = scroller.scrollLeft;
        }
      }
    };
    // Initial check
    updateScroller();

    // Runs everytime the DOM changes
    const observer = new MutationObserver(updateScroller);
    observer.observe(rootElement, { childList: true, subtree: true });

    const resizeObserver = new window.ResizeObserver(updateScroller);
    resizeObserver.observe(rootElement);

    window.addEventListener('resize', updateScroller);

    return () => {
      observer.disconnect();
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateScroller);
    };
  }, [apiRef, columns, products]);

  // Sync scroll positions for each bar
  // Responsible for moving the bars together
  useEffect(() => {
    if (!dataGridScroller || !topScrollRef.current) return;
    const topScrollDiv = topScrollRef.current;
    //Update top scroll bar width based on MUI scrollbar width
    const handleDataGridScroll = () => {
      if (topScrollDiv.scrollLeft !== dataGridScroller.scrollLeft) {
        topScrollDiv.scrollLeft = dataGridScroller.scrollLeft;
      }
    };

    //Update mui scroll bar width based on top scrollbar width
    const handleTopScroll = () => {
      if (dataGridScroller.scrollLeft !== topScrollDiv.scrollLeft) {
        dataGridScroller.scrollLeft = topScrollDiv.scrollLeft;
      }
    };

    // Attaches sync functions to scrolling events
    dataGridScroller.addEventListener('scroll', handleDataGridScroll);
    topScrollDiv.addEventListener('scroll', handleTopScroll);

    return () => {
      dataGridScroller.removeEventListener('scroll', handleDataGridScroll);
      topScrollDiv.removeEventListener('scroll', handleTopScroll);
    };
  }, [apiRef, columns, products, dataGridScroller, topScrollRef]);

  return (
    <div
      ref={topScrollRef}
      style={{
        overflowX: 'auto',
        position: 'sticky',
        top: 0,
        zIndex: 1200,
        width: '100%',
        display: showTopScrollbar ? 'block' : 'none',
        height: '15px',
      }}
    >
      <div style={{ height: '1px', width: `${dataGridContentWidth}px`, background: 'transparent' }} />
    </div>
  );
};

export default SyncedScrollBar;
